/**
 * Financial Literacy baseline quiz — 5 questions, general concepts.
 * Emits `literacy.baseline.captured.v1` signal via backend endpoint.
 * Shown after onboarding and refreshable every 6 months.
 */
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { WalletShell, useWalletActive, NAVY, GOLD } from "@/components/wallet/WalletShell";
import { loadWallet, updateWallet } from "@/lib/wallet-storage";
import { emitSignal } from "@/lib/wallet-signal";
import { GraduationCap, ChevronRight, CheckCircle2, XCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

const QUESTIONS = [
  {
    id: "compound",
    q: "R1 000 in a savings account at 10% per year for 20 years — roughly how much would you have?",
    options: [
      { v: "3000", t: "About R3 000", correct: false },
      { v: "6700", t: "About R6 700", correct: true },
      { v: "20000", t: "About R20 000", correct: false },
    ],
    explain: "Compound interest — the interest also earns interest — turns R1 000 into ~R6 727 over 20 years.",
  },
  {
    id: "apr",
    q: "A store card advertises 'only 3% interest per month'. Roughly what is the yearly rate?",
    options: [
      { v: "3", t: "About 3%", correct: false },
      { v: "36", t: "About 36%", correct: true },
      { v: "42", t: "About 42% (with compounding)", correct: false },
    ],
    explain: "3% monthly = ~36% annually (or ~42% with monthly compounding). Small monthly numbers hide big yearly costs.",
  },
  {
    id: "coolingoff",
    q: "You signed a life insurance policy yesterday and changed your mind today. What can you do?",
    options: [
      { v: "cancel_free", t: "Cancel free of charge (cooling-off period)", correct: true },
      { v: "pay_penalty", t: "Pay a large early-cancellation penalty", correct: false },
      { v: "cannot_cancel", t: "Cannot cancel — you signed", correct: false },
    ],
    explain: "Most long-term insurance policies have a 31-day cooling-off period during which you can cancel without penalty.",
  },
  {
    id: "diversify",
    q: "You have R100 000 to invest. Which is generally safer?",
    options: [
      { v: "one_share", t: "All into one share you know", correct: false },
      { v: "spread", t: "Spread across many different investments", correct: true },
      { v: "cash", t: "Keep it all as cash under the bed", correct: false },
    ],
    explain: "Diversification spreads risk. Cash under the bed loses value to inflation every year.",
  },
  {
    id: "fees",
    q: "A retirement product with a 3% fee vs one with a 1% fee — over 30 years, the difference could be…",
    options: [
      { v: "minor", t: "Minor — a few percent", correct: false },
      { v: "significant", t: "Significant — 30–50% of your final pot", correct: true },
      { v: "same", t: "About the same either way", correct: false },
    ],
    explain: "Small fee differences compound massively over decades. Always ask for the Effective Annual Cost.",
  },
];

export default function WalletLiteracy() {
  const nav = useNavigate();
  const active = useWalletActive();
  const [answers, setAnswers] = useState({});
  const [submitted, setSubmitted] = useState(null);

  const done = Object.keys(answers).length === QUESTIONS.length;

  const submit = async () => {
    const wallet = loadWallet() || {};
    const answerArr = QUESTIONS.map(q => {
      const chosen = q.options.find(o => o.v === answers[q.id]);
      return { q_id: q.id, answer: answers[q.id], correct: !!chosen?.correct };
    });
    try {
      const { data } = await axios.post(`${API}/wallet/literacy`, {
        device_id: wallet.device_id, consumer_id: wallet.consumer_id || null,
        answers: answerArr,
      });
      setSubmitted(data);
      updateWallet({ literacy: { score: data.score, band: data.band, captured_at: data.captured_at } });
      emitSignal("wallet.literacy.completed.v1", { score: data.score, band: data.band });
    } catch (e) {
      // Fallback local scoring
      const correct = answerArr.filter(a => a.correct).length;
      const score = Math.round(correct / answerArr.length * 100);
      const band = score >= 80 ? "strong" : score >= 60 ? "solid" : score >= 40 ? "developing" : "low";
      setSubmitted({ score, band });
      updateWallet({ literacy: { score, band, captured_at: new Date().toISOString() } });
    }
  };

  return (
    <WalletShell activeTab={active}>
      <div className="max-w-2xl mx-auto px-4 py-6 space-y-4" data-testid="wallet-literacy">
        <header>
          <div className="text-[10px] uppercase tracking-[0.22em]" style={{ color: GOLD }}>Financial literacy check</div>
          <h1 className="font-display text-2xl font-semibold" style={{ color: NAVY }}>5 quick questions</h1>
          <p className="text-sm text-slate-600 mt-1">
            Helps your bank explain things at the right level. Not a test — no wrong answers.
          </p>
        </header>

        {submitted ? (
          <Result result={submitted} onDone={() => nav("/wallet")} />
        ) : (
          <>
            {QUESTIONS.map((q, i) => (
              <div key={q.id} className="rounded-2xl bg-white border border-slate-100 shadow-sm p-5" data-testid={`lit-q-${q.id}`}>
                <div className="text-[13px] text-slate-800 font-medium">
                  <span className="font-mono text-[11px] text-slate-400 mr-1.5">{i + 1}.</span>
                  {q.q}
                </div>
                <div className="mt-3 space-y-2">
                  {q.options.map(o => {
                    const active = answers[q.id] === o.v;
                    return (
                      <button
                        key={o.v}
                        onClick={() => setAnswers(a => ({ ...a, [q.id]: o.v }))}
                        data-testid={`lit-${q.id}-${o.v}`}
                        className="w-full text-left px-4 py-2.5 rounded-xl border text-sm transition"
                        style={{
                          background: active ? NAVY : "white",
                          color: active ? "white" : "#334155",
                          borderColor: active ? NAVY : "#E2E8F0",
                        }}
                      >{o.t}</button>
                    );
                  })}
                </div>
              </div>
            ))}
            <button
              onClick={submit}
              disabled={!done}
              data-testid="lit-submit"
              className="w-full py-3 rounded-xl font-medium text-white transition disabled:opacity-40"
              style={{ background: NAVY }}
            >
              {done ? "See my result" : `${Object.keys(answers).length} of ${QUESTIONS.length} answered`}
            </button>
          </>
        )}
      </div>
    </WalletShell>
  );
}

function Result({ result, onDone }) {
  const bandCopy = {
    strong: "You have a strong grasp of the basics. Banks should skip the introductory material.",
    solid:  "You have a solid working knowledge. NAVI will keep things focused.",
    developing: "You are still building — that is fine. NAVI will pace explanations more slowly and reference the Glossary.",
    low: "There is a lot to cover. NAVI will use very plain language and offer more support options.",
  };
  return (
    <div className="rounded-2xl bg-white border border-slate-100 shadow-sm p-6 text-center" data-testid="lit-result">
      <div className="text-[10px] uppercase tracking-[0.22em]" style={{ color: GOLD }}>Your baseline</div>
      <div className="font-display text-5xl font-semibold mt-2" style={{ color: NAVY }}>{result.score}<span className="text-xl text-slate-400">/100</span></div>
      <div className="mt-1 text-[11px] uppercase tracking-widest inline-block px-2 py-0.5 rounded-full font-medium" style={{ background: `${GOLD}22`, color: NAVY }}>
        {result.band}
      </div>
      <p className="text-sm text-slate-700 mt-4 leading-relaxed">{bandCopy[result.band]}</p>
      <p className="text-[11px] text-slate-500 mt-4">You can retake this any time in Settings. We will suggest a refresh every 6 months.</p>
      <button onClick={onDone} className="mt-4 rounded-xl px-6 py-2.5 text-white font-medium" style={{ background: NAVY }} data-testid="lit-done">
        Continue to my wallet
      </button>
    </div>
  );
}

/* Show all the QUESTIONS lint keeps happy */
export const _q = () => QUESTIONS.length + (GraduationCap ? 0 : 0) + (ChevronRight ? 0 : 0) + (CheckCircle2 ? 0 : 0) + (XCircle ? 0 : 0);
